import React, { useState } from "react";
import axios from "axios";
import API_BASE from "../utils/api";
import Modal from "./Modal";

// Bulk-imports opening balances/arrears from a spreadsheet exported by
// whatever system the school used before this one. Modeled directly on
// BulkAttendanceImportModal — file input, template download, results table.
const OpeningBalanceImportModal = ({ onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const downloadTemplate = () => {
    const rows = [
      ["rollNumber", "admissionNumber", "amount", "dueDate", "remarks"],
      ["R001", "", "5000", "2026-04-10", "Carried forward from previous system"],
    ];
    const csv = rows.map((r) => r.join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "opening_balance_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setError("");
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API_BASE}/fees/opening-balance/import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      onImported && onImported();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to import file");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal title="Import Opening Balances" subtitle="Bring forward arrears from before this system" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Upload an Excel (.xlsx/.xls) file. Each row identifies a student by <strong>rollNumber</strong> or <strong>admissionNumber</strong>,
          plus an <strong>amount</strong>. <strong>dueDate</strong> defaults to today; <strong>remarks</strong> is optional. Each row becomes a
          normal payable fee record — no separate payment flow needed.
        </p>
        <button type="button" onClick={downloadTemplate} className="text-sm text-indigo-600 hover:underline">
          Download CSV template
        </button>
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => { setFile(e.target.files[0] || null); setResult(null); setError(""); }}
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
        {result && (
          <div className="space-y-2">
            <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
              {result.created} of {result.total} record(s) imported successfully.
              {result.failed > 0 && ` ${result.failed} failed.`}
            </div>
            {result.failed > 0 && (
              <div className="max-h-40 overflow-y-auto border rounded-lg divide-y text-sm">
                {result.results.filter((r) => r.status === "error").map((r, i) => (
                  <div key={i} className="px-3 py-2 text-red-600">
                    Row {r.row} ({r.identifier || "unknown"}): {r.error}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100">Close</button>
          <button
            type="button"
            onClick={handleImport}
            disabled={!file || importing}
            className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {importing ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default OpeningBalanceImportModal;
